import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

/**
 * AddRemoveMessage Component
 * 
 * Displays add-remove type messages as centered system messages.
 * If miniConversationId is present, shows "click to view or respond" at the bottom
 * and makes the message clickable to open the notes action sheet.
 */
const AddRemoveMessage = ({ message, currentUser, storedUserId, onOpenNotes }) => {
  const theme = useTheme();
  
  // Get current user ID
  const currentUserId = currentUser?.userId ?? storedUserId;
  
  // Get user order data
  const orderId = message?.orderId;
  const groupOrder = orderId?.groupOrder;
  const userOrders = groupOrder?.userOrders || [];
  
  // Find current user's order
  const currentUserOrder = userOrders.find(userOrder => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === currentUserId;
  });
  
  // Check if user has chosen split per person
  const hasUserChosenSplitPerPerson = currentUserOrder?.paymentType === "split_per_person";
  
  // Hide message if user has chosen split per person
  if (hasUserChosenSplitPerPerson) {
    return null;
  }

  // Check if miniConversationId exists - if so, show clickable message with "click to view or respond"
  const hasMiniConversation = message?.miniConversationId != null;

  // Handle click to open notes action sheet
  const handleClick = () => {
    if (hasMiniConversation && message.miniConversationId && onOpenNotes) {
      onOpenNotes(message);
    }
  };
  
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        my: 2,
      }}
    >
      <Box
        onClick={hasMiniConversation ? handleClick : undefined}
        sx={{
          backgroundColor: hasMiniConversation ? "#e9d5ff" : "#f3f4f6", // Light purple for mini conversation, gray otherwise
          color: "#374151",
          padding: theme.spacing(1.5, 2),
          borderRadius: theme.spacing(2),
          fontSize: "14px",
          fontWeight: 500,
          textAlign: "center",
          maxWidth: "80%",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e5e7eb",
          cursor: hasMiniConversation ? "pointer" : "default",
          "&:hover": hasMiniConversation ? {
            backgroundColor: "#ddd6fe",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
          } : {},
        }}
      >
        {/* Main message text */}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#374151",
            marginBottom: hasMiniConversation ? theme.spacing(0.5) : 0,
            textAlign: "center",
          }}
        >
          {message.message || message.text || "System message"}
        </Typography>
        
        {/* Show "click to view or respond" if miniConversationId exists */}
        {hasMiniConversation && (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "#9ca3af", // Lighter gray color
              marginTop: theme.spacing(0.5),
              textAlign: "center",
            }}
          >
            click to view or respond
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AddRemoveMessage;

